import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PageShell } from '../components/Layout';
import { DemoSignInModal } from '../components/DemoSignInModal';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useFlowClient } from '../hooks/useFlowClient';
import { useDemoGuard } from '../hooks/useDemoGuard';
import { trackEvent } from '../utils/clarity';


const Upload: React.FC = () => {
  const { projects, setProjects, setActivities } = useApp();
  const { userEmail } = useAuth();
  const flowClient = useFlowClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { project_id } = useParams<{ project_id: string }>();

  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    project_id ?? (location.state as any)?.projectId ?? ''
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sort: projects without a schedule float to the top. Viewer-role projects are
  // excluded entirely — Viewers shouldn't see them as upload options at all.
  const sortedProjects = [...projects]
    .filter((p) => p.role_on_project !== 'Viewer')
    .sort((a, b) => {
      if (!a.last_upload_date && b.last_upload_date) return -1;
      if (a.last_upload_date && !b.last_upload_date) return 1;
      return 0;
    });

  // Looked up from the full projects list (not sortedProjects) so a Viewer-role
  // project reached via direct URL still resolves — needed to show the view-only
  // note below instead of a blank/broken form.
  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const isViewerOnSelected = selectedProject?.role_on_project === 'Viewer';
  const { gateOpen, gateFeature, closeGate, guard } = useDemoGuard();

  useEffect(() => { trackEvent('upload_viewed'); }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !['xer', 'xlsx'].includes(ext)) {
      setError('Please select a .xer or .xlsx file.');
      return;
    }
    setError(null);
    setSuccess(false);
    setSelectedFile(file);
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file.'));
      reader.readAsDataURL(file);
    });

  const handleUpload = async () => {
    if (!selectedFile || !selectedProjectId || !userEmail) return;

    setUploading(true);
    setError(null);
    setSuccess(false);
    setProgress(10);

    try {
      setProgress(30);
      const fileContent = await readFileAsBase64(selectedFile);
      setProgress(60);

      await flowClient.upload(userEmail, selectedProjectId, selectedFile.name, fileContent);
      setProgress(100);

      // Clear cached activities so all screens re-fetch fresh data
      setActivities([]);

      // Refresh projects to pick up new last_upload_date
      const refreshed = await flowClient.projects(userEmail!);
      setProjects(refreshed.projects);

      setSuccess(true);
      const uploadedProjectId = selectedProjectId;
      setSelectedFile(null);
      setSelectedProjectId('');
      if (fileRef.current) fileRef.current.value = '';

      // Navigate to the project dashboard after a short delay
      setTimeout(() => navigate(`/project/${uploadedProjectId}/dashboard`), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1500);
    }
  };

  const canUpload = !!selectedProjectId && !!selectedFile && !uploading && !isViewerOnSelected;

  return (
    <PageShell className="mt-4 space-y-4 pb-10">
        {/* Instructions */}
        <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <p className="text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide mb-3">
            How to upload
          </p>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-accent text-white text-xs font-bold flex items-center justify-center shrink-0">
                1
              </div>
              <div>
                <p className="text-sm font-semibold text-text dark:text-gray-100">Select a project</p>
                <p className="text-[15px] text-muted dark:text-gray-400">
                  Choose the project this schedule belongs to.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-accent text-white text-xs font-bold flex items-center justify-center shrink-0">
                2
              </div>
              <div>
                <p className="text-sm font-semibold text-text dark:text-gray-100">Select your P6 file</p>
                <p className="text-[15px] text-muted dark:text-gray-400">
                  Accepts P6 .xer exports and Excel .xlsx schedules.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 1 — Project */}
        <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <label className="block text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide mb-2">
            Step 1 — Select project
          </label>

          {projects.length === 0 ? (
            <p className="text-xs text-muted dark:text-gray-400">
              No projects found. Your administrator will need to create one first.
            </p>
          ) : (
            <div ref={dropdownRef} className="relative">
              {/* Trigger button */}
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                className={`w-full flex items-center justify-between gap-2 text-sm border rounded-[8px] px-3 py-2.5 bg-white dark:bg-gray-700 text-left min-h-[44px] transition-colors ${
                  dropdownOpen
                    ? 'border-accent ring-2 ring-accent/20'
                    : 'border-gray-200 dark:border-gray-600'
                }`}
              >
                <span className={selectedProject ? 'text-text dark:text-gray-100' : 'text-muted dark:text-gray-400'}>
                  {selectedProject ? selectedProject.project_name : 'Choose a project…'}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {selectedProject && !selectedProject.last_upload_date && (
                    <span className="text-xs font-semibold text-watch-text bg-watch-bg px-2 py-0.5 rounded-full">
                      No schedule
                    </span>
                  )}
                  <svg className={`w-4 h-4 text-muted transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Dropdown list */}
              {dropdownOpen && (
                <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-[8px] shadow-lg overflow-hidden">
                  {sortedProjects.map((p) => {
                    const missing = !p.last_upload_date;
                    return (
                      <React.Fragment key={p.id}>
                        <button
                          type="button"
                          onClick={() => { setSelectedProjectId(p.id); setDropdownOpen(false); }}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            selectedProjectId === p.id ? 'bg-tint dark:bg-green-900/20' : missing ? 'bg-ground' : ''
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-text dark:text-gray-100 truncate">{p.project_name}</p>
                            <p className="text-xs text-muted dark:text-gray-400 truncate">{p.gc_company}</p>
                          </div>
                          {missing ? (
                            <span className="shrink-0 text-xs font-semibold text-watch-text bg-watch-bg border border-watch-text/20 px-2 py-0.5 rounded-full">
                              No schedule
                            </span>
                          ) : (
                            <span className="shrink-0 text-xs text-muted dark:text-gray-400 whitespace-nowrap">
                              ✓ Uploaded
                            </span>
                          )}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Viewer-role project reached directly (e.g. via URL) — show a view-only
            note instead of a file-upload form that could never succeed. Demo mode uses
            the same form as a real PM (its one project is a real, if fictional, PM-role
            project) — only the actual submit is intercepted, via useDemoGuard below. */}
        {isViewerOnSelected ? (
          <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <p className="text-sm text-muted dark:text-gray-400">
              View only — you have Viewer access to <strong>{selectedProject?.project_name}</strong> and
              cannot upload a schedule for this project. Contact your PM for upload access.
            </p>
          </div>
        ) : (
          <>
            {/* Step 2 — File */}
            <div className={`bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4 transition-opacity ${!selectedProjectId ? 'opacity-40 pointer-events-none' : ''}`}>
              <label className="block text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide mb-2">
                Step 2 — Select schedule file
              </label>

              <button
                onClick={() => guard(() => fileRef.current?.click(), 'upload your own schedule')}
                className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-[8px] p-6 text-center hover:border-accent hover:bg-tint dark:hover:bg-green-900/10 transition-colors"
              >
                <div className="text-3xl mb-2">📁</div>
                <p className="text-sm font-semibold text-text dark:text-gray-100">
                  {selectedFile ? selectedFile.name : 'Tap to choose file'}
                </p>
                <p className="text-xs text-muted dark:text-gray-400 mt-1">
                  {selectedFile
                    ? `${(selectedFile.size / 1024).toFixed(1)} KB · ${selectedFile.name.split('.').pop()?.toUpperCase()}`
                    : 'Accepts .xer and .xlsx'}
                </p>
              </button>

              <input
                ref={fileRef}
                type="file"
                accept=".xer,.xlsx"
                onChange={handleFileChange}
                className="hidden"
              />

              {selectedFile && (
                <div className="mt-3 flex items-center justify-between bg-tint rounded-[8px] px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-accent">{selectedFile.name}</p>
                    <p className="text-xs text-muted">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileRef.current) fileRef.current.value = '';
                    }}
                    className="text-xs text-crit-text font-semibold min-h-[36px] px-2"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Progress bar */}
            {uploading && progress > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-[8px] p-4 border border-gray-100 dark:border-gray-700">
                <div className="flex justify-between text-xs font-medium mb-1.5">
                  <span className="text-text dark:text-gray-100">Uploading…</span>
                  <span className="text-accent dark:text-green-400">{progress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-link rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-crit-bg border border-crit-text rounded-[8px] p-4">
                <p className="text-sm font-medium text-crit-text mb-1">Upload failed</p>
                <p className="text-xs text-crit-text">{error}</p>
                <button
                  onClick={() => guard(handleUpload, 'upload your own schedule')}
                  className="mt-2 text-xs font-semibold text-crit-text border border-crit-text px-3 py-1.5 rounded-[8px] min-h-[36px]"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Success */}
            {success && (
              <div className="bg-tint border border-accent rounded-[8px] p-4 fade-in">
                <p className="text-sm font-semibold text-accent mb-1">Upload successful!</p>
                <p className="text-xs text-muted">
                  Schedule data is live. Taking you to the project dashboard…
                </p>
              </div>
            )}

            {/* Upload button */}
            {canUpload && !success && (
              <button
                onClick={() => guard(handleUpload, 'upload your own schedule')}
                className="w-full bg-accent hover:bg-link text-white font-semibold py-3.5 rounded-[8px] text-sm transition-colors min-h-[52px] shadow-sm fade-in"
              >
                Upload schedule
              </button>
            )}

            {!canUpload && !success && !uploading && (
              <p className="text-xs text-center text-muted dark:text-gray-400">
                {!selectedProjectId && !selectedFile
                  ? 'Complete both steps above to upload.'
                  : !selectedProjectId
                  ? 'Select a project to continue.'
                  : 'Select a file to continue.'}
              </p>
            )}
          </>
        )}
      
      <DemoSignInModal open={gateOpen} onClose={closeGate} feature={gateFeature} />
    </PageShell>
  );
};

export default Upload;
